// frontend/src/pages/ChatList.jsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import chatService from "../services/chatService";
import presenceService from "../services/presenceService";
import { MessageCircle,  Loader2, ArrowLeft } from "lucide-react";

export default function ChatList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [presenceMap, setPresenceMap] = useState({});

  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
    const interval = setInterval(() => {
      fetchConversations();
      fetchUnreadCount();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await chatService.getConversations();
      setConversations(response.data);
      
      // Obtener presencia de todos los usuarios en las conversaciones
      const userIds = response.data.map(conv => {
        const otherUser = conv.buyer_id === user?.id ? conv.seller_id : conv.buyer_id;
        return otherUser;
      });
      
      if (userIds.length > 0) {
        fetchAllPresence(userIds);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPresence = async (userIds) => {
    try {
      const promises = userIds.map(id => presenceService.getUserPresence(id));
      const results = await Promise.all(promises);
      
      const map = {};
      userIds.forEach((id, index) => {
        map[id] = results[index];
      });
      setPresenceMap(map);
    } catch (error) {
      console.error("Error fetching presence:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await chatService.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const getOtherUser = (conversation) => {
    return conversation.buyer_id === user?.id ? conversation.seller : conversation.buyer;
  };

  const getOtherUserId = (conversation) => {
    return conversation.buyer_id === user?.id ? conversation.seller_id : conversation.buyer_id;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const diff = new Date() - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Agora";
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 text-[#449879] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-2 pb-24 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* ===== HEADER CON BOTÓN DE VOLVER ===== */}
          <div className="p-6 border-b border-gray-100 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              title="Voltar"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <MessageCircle className="w-6 h-6 text-[#449879]" />
                Minhas Conversas
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {conversations.length} conversa(s) ativa(s)
                {unreadCount > 0 && (
                  <span className="ml-2 text-red-500 font-medium">
                    • {unreadCount} não lida(s)
                  </span>
                )}
              </p>
            </div>
          </div>

          {conversations.length === 0 ? (
            <div className="p-12 text-center">
              <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nenhuma conversa ainda</p>
              <p className="text-sm text-gray-400 mt-1">
                Quando você mostrar interesse em um produto, a conversa aparecerá aqui.
              </p>
              <Link
                to="/"
                className="mt-4 inline-block bg-[#449879] text-white px-6 py-2 rounded-xl hover:bg-[#367d64] transition-colors"
              >
                Explorar produtos
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {conversations.map((conv) => {
                const otherUser = getOtherUser(conv);
                const otherUserId = getOtherUserId(conv);
                const unreadMessages = conv.unread_messages || [];
                const unreadCount = unreadMessages.length;
                const presence = presenceMap[otherUserId];
                const isOnline = presence?.is_online || false;

                return (
                  <Link
                    key={conv.id}
                    to={`/chat/${conv.id}`}
                    className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                  >
                    {/* Avatar con indicador de online */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#449879] to-[#2d5f4d] rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {otherUser?.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      {/* Indicador de presencia */}
                      <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                        isOnline ? 'bg-green-500' : 'bg-gray-400'
                      }`}></span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-800 truncate">
                            {otherUser?.name || "Usuário"}
                          </h3>
                          <span className={`text-[10px] font-medium ${
                            isOnline ? 'text-green-600' : 'text-gray-400'
                          }`}>
                            {isOnline ? '● Online' : '● Offline'}
                          </span>
                        </div>
                        {conv.last_message_at && (
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                            {formatDate(conv.last_message_at)}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-gray-500 truncate">
                          {conv.last_message?.message || "Nenhuma mensagem"}
                        </p>
                        {unreadCount > 0 && (
                          <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                            {unreadCount}
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-400 mt-1 truncate">
                        📦 {conv.post?.titulo}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}